/* global cashflowForecastOverridesUrl cashflowForecastOverridesUrl */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

import axios from 'axios';

import { COMMITMENT_VIEWS } from './commitmentViews';

const CashflowForecastReportTableContext = createContext(null);

export function CashflowForecastReportTableProvider({ children, onRefetch }) {
    const [collapsed, setCollapsed] = useState({
        income: false,
        expenditure: false,
        tax: false,
    });

    // Committed / Uncommitted / Total view selection. Purely a client-side choice over the
    // one payload — in-memory only, so it resets on reload.
    const [view, setView] = useState(COMMITMENT_VIEWS.TOTAL);

    // Track whether the VAT/GST row is expanded to show children (Collected/Paid/Remitted)
    const [vatGstExpanded, setVatGstExpanded] = useState(false);

    const toggleVatGst = useCallback(() => {
        setVatGstExpanded((prev) => !prev);
    }, []);

    const [deptExpandTrigger, setDeptExpandTrigger] = useState({ version: 0, expand: false });

    // Map of monthKey → pending save state ('saving' | 'saved' | 'error')
    const [pendingOverrides, setPendingOverrides] = useState({});

    // Set of month keys that currently have an override stored
    const [overriddenMonths, setOverriddenMonths] = useState(new Set());

    // Track inflight save requests so we can cancel stale ones
    const saveAbortControllers = useRef({});

    const toggleSection = useCallback((section) => {
        setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    const expandAll = useCallback(() => {
        setCollapsed({ income: false, expenditure: false, tax: false });
        setVatGstExpanded(true);
        setDeptExpandTrigger((prev) => ({ version: prev.version + 1, expand: true }));
    }, []);

    const collapseAll = useCallback(() => {
        setCollapsed({ income: true, expenditure: true, tax: true });
        setVatGstExpanded(false);
        setDeptExpandTrigger((prev) => ({ version: prev.version + 1, expand: false }));
    }, []);

    /**
     * Save (or delete) a closing balance override for a month.
     * amount = null or '' means delete the override.
     */
    const saveOverride = useCallback(
        (monthKey, amount) => {
            // Cancel any in-flight request for this month
            if (saveAbortControllers.current[monthKey]) {
                saveAbortControllers.current[monthKey].abort();
            }
            const controller = new AbortController();
            saveAbortControllers.current[monthKey] = controller;

            setPendingOverrides((prev) => ({ ...prev, [monthKey]: 'saving' }));

            const payload = { month_key: monthKey, amount: amount === '' ? null : amount };

            axios
                .post(cashflowForecastOverridesUrl, payload, { signal: controller.signal })
                .then((res) => {
                    setPendingOverrides((prev) => ({ ...prev, [monthKey]: 'saved' }));

                    if (res.data.deleted) {
                        setOverriddenMonths((prev) => {
                            const next = new Set(prev);
                            next.delete(monthKey);
                            return next;
                        });
                    } else {
                        setOverriddenMonths((prev) => new Set([...prev, monthKey]));
                    }

                    // Refetch to get recalculated rolling balances
                    if (onRefetch) {
                        onRefetch();
                    }
                })
                .catch((err) => {
                    if (axios.isCancel(err)) return;
                    setPendingOverrides((prev) => ({ ...prev, [monthKey]: 'error' }));
                });
        },
        [onRefetch],
    );

    /**
     * Reset all overrides for the current budget.
     */
    const resetAllOverrides = useCallback(() => {
        axios
            .delete(cashflowForecastOverridesUrl)
            .then(() => {
                setOverriddenMonths(new Set());
                setPendingOverrides({});

                // Refetch to get recalculated rolling balances
                if (onRefetch) {
                    onRefetch();
                }
            })
            .catch(() => {
                // silently fail — user can retry
            });
    }, [onRefetch]);

    /**
     * Delete overrides for a specific set of month keys (used when start month changes
     * and some overrides fall before the new window).
     *
     * @param {string[]} monthKeys
     * @returns {Promise}
     */
    const deleteOverridesForMonths = useCallback(
        (monthKeys) => {
            if (!monthKeys || monthKeys.length === 0) {
                return Promise.resolve();
            }

            // Check if ALL overridden months are in the list — if so use the bulk DELETE
            const allMonthsAffected = monthKeys.every((k) => overriddenMonths.has(k)) && monthKeys.length >= overriddenMonths.size;

            if (allMonthsAffected) {
                return axios.delete(cashflowForecastOverridesUrl).then(() => {
                    setOverriddenMonths(new Set());
                    setPendingOverrides({});
                });
            }

            // Otherwise delete each affected month individually (amount: null = delete)
            return Promise.all(
                monthKeys.map((monthKey) =>
                    axios
                        .post(cashflowForecastOverridesUrl, { month_key: monthKey, amount: null })
                        .then(() => {
                            setOverriddenMonths((prev) => {
                                const next = new Set(prev);
                                next.delete(monthKey);
                                return next;
                            });
                            setPendingOverrides((prev) => {
                                const next = { ...prev };
                                delete next[monthKey];
                                return next;
                            });
                        })
                        .catch(() => {}),
                ),
            );
        },
        [overriddenMonths],
    );

    /**
     * Initialise overriddenMonths from loaded forecast data.
     * Called after a successful data fetch so the set stays in sync with server state.
     */
    const syncOverriddenMonths = useCallback((monthlyTotals) => {
        if (!monthlyTotals) return;
        const overridden = new Set(
            Object.entries(monthlyTotals)
                .filter(([, totals]) => totals.closingBalanceOverride != null)
                .map(([monthKey]) => monthKey),
        );
        setOverriddenMonths(overridden);
    }, []);

    return (
        <CashflowForecastReportTableContext.Provider
            value={{
                view,
                setView,
                collapsed,
                toggleSection,
                expandAll,
                collapseAll,
                vatGstExpanded,
                toggleVatGst,
                deptExpandTrigger,
                pendingOverrides,
                overriddenMonths,
                saveOverride,
                resetAllOverrides,
                deleteOverridesForMonths,
                syncOverriddenMonths,
            }}
        >
            {children}
        </CashflowForecastReportTableContext.Provider>
    );
}

export function useCashflowForecastTableContext() {
    const ctx = useContext(CashflowForecastReportTableContext);
    if (!ctx) {
        throw new Error('useCashflowForecastTableContext must be used within CashflowForecastReportTableProvider');
    }
    return ctx;
}
